import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'

type DownloadChecklistDocumentVariables = {
  documentId: number
  documentName: string
}

/**
 * Download the most recently uploaded file(s) for a given checklist document.
 *
 * @returns A UseMutationResult object for triggering the download.
 */
export const useDownloadChecklistDocument = (): UseMutationResult<
  void,
  Error,
  DownloadChecklistDocumentVariables
> => {
  const api = useApiWithIDToken()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    DownloadChecklistDocumentVariables,
    { toastId: string }
  >({
    mutationFn: ({ documentId, documentName }) =>
      downloadChecklistDocument(api, documentId, documentName),
    onMutate: (vars) => ({
      toastId: startProgress('Downloading Document', vars.documentName),
    }),
    onSuccess: (_data, vars) => {
      sendNotification({
        title: 'Download Successful',
        message: `${vars.documentName} has been successfully downloaded.`,
        useRucksackUI: true,
      })
    },
    onError: (_data, vars) => {
      sendNotification({
        title: 'Download Failed',
        message: `An error occurred while trying to download ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function downloadChecklistDocument(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  documentName: string
): Promise<void> {
  try {
    const url = `/documents/${documentId}`
    const response = await api.get<{ download: string }>(url)
    const { download } = response.data

    // Create and click an anchor link to trigger the download
    const link = document.createElement('a')
    link.href = download
    link.download = documentName
    link.click()
  } catch (error) {
    throw new Error(
      `Failed to download file(s) for document ${documentName}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}
